import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, HeartHandshake } from "lucide-react";
import { SectionHeading } from "../../components/shared/SectionHeading";
import { IMAGES, FORMULES_COORDINATION } from "../../data/mockData";
import { usePageMedia } from "../../lib/usePageMedia";
import { usePageTexts } from "../../lib/usePageTexts";
import { ReviewsSection } from "../../components/shared/ReviewsSection";
import { api } from "../../lib/api";

export default function CoordinationPage() {
  const pageMedia = usePageMedia();
  const { text } = usePageTexts();
  const [offers, setOffers] = useState(null);
  useEffect(() => {
    api.get("/coordination/offers").then((r) => setOffers(r.data)).catch(() => setOffers([]));
  }, []);
  // Formules administrées (CMS) ; repli sur le contenu intégré si aucune n'est publiée
  const formules = offers && offers.length
    ? offers.map((o) => ({ key: o.key, name: o.name, price: o.price_ttc, desc: o.description, includes: o.inclusions, accroche: o.accroche, badge: o.badge, cta: o.cta_label, day_j_label: o.day_j_label }))
    : FORMULES_COORDINATION.map((f, i) => ({ key: i + 1, name: f.name, price: f.price, desc: f.desc, includes: f.includes, day_j_label: f.dayJ ? "Présence le jour J, jusqu'au dessert" : "Sans présence le jour J" }));
  return (
    <div data-testid="coordination-page">
      <section className="relative flex min-h-[60vh] items-center overflow-hidden">
        <img src={pageMedia["coordination:hero"]?.url || IMAGES.coordination} alt={pageMedia["coordination:hero"]?.alt || "Coordination de mariage Art Event"} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-deepgreen/50" />
        <div className="container-ae relative py-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-accent text-3xl text-gold-light">Coordination de mariage</p>
            <h1 className="mt-4 max-w-2xl text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white" data-testid="coordination-title">
              Un mariage serein, orchestré pour vous
            </h1>
            <p className="mt-5 max-w-xl text-lg text-white/85 leading-relaxed">
              {text("coordination:intro", "Une prestation indépendante et facultative — vous pouvez aussi simplement louer notre matériel, sans aucune formule de coordination.")}
            </p>
            <Link to="/rendez-vous" data-testid="coordination-cta-appointment" className="btn-primary-ae mt-8 !bg-gold !text-deepgreen hover:!bg-gold-hover">
              Échanger sur mon mariage
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="coordination-formules">
        <div className="container-ae">
          <SectionHeading overline="Nos formules" title="Trois accompagnements," accent="un seul objectif" align="center">
            Chaque formule débute par un rendez-vous avec Art Event, avant tout engagement.
          </SectionHeading>
          {offers === null ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Chargement des formules…</p>
          ) : (
            <div className="grid items-start gap-8 pt-2 lg:grid-cols-3">
              {formules.map((f, i) => (
                <motion.div
                  key={f.key}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12, duration: 0.5 }}
                  className={`card-ae relative flex flex-col p-8 ${f.badge ? "border-2 border-gold shadow-lg" : ""}`}
                  data-testid={`formule-${f.key}`}
                >
                  {f.badge && (
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold px-4 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-deepgreen" data-testid={`formule-badge-${f.key}`}>
                      {f.badge}
                    </span>
                  )}
                  <h3 className="font-heading text-2xl text-deepgreen">{f.name}</h3>
                  <p className="mt-3 text-4xl font-heading font-semibold text-deepgreen">
                    {Number(f.price).toLocaleString("fr-FR")} € <span className="text-sm font-body font-normal text-muted-foreground">TTC</span>
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                  {f.includes?.length > 0 && (
                    <ul className="mt-5 flex-1 space-y-2.5 text-sm text-foreground/80">
                      {f.includes.map((inc, j) => (
                        <li key={j} className="flex items-start gap-2.5">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" aria-hidden /> {inc}
                        </li>
                      ))}
                    </ul>
                  )}
                  {f.day_j_label && (
                    <p className="mt-5 flex items-center gap-2 rounded-lg bg-sage-light px-3 py-2 text-xs font-semibold text-deepgreen" data-testid={`formule-dayj-${f.key}`}>
                      <HeartHandshake className="h-4 w-4 shrink-0" aria-hidden /> {f.day_j_label}
                    </p>
                  )}
                  {f.accroche && (
                    <p className="mt-5 font-accent text-xl leading-snug text-deepgreen/85" data-testid={`formule-accroche-${f.key}`}>« {f.accroche} »</p>
                  )}
                  <Link to="/rendez-vous" data-testid={`formule-${f.key}-cta`} className={f.badge ? "btn-primary-ae mt-7" : "btn-secondary-ae mt-7"}>
                    {f.cta || "Prendre rendez-vous"}
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
          <p className="mt-8 text-center text-xs text-muted-foreground" data-testid="coordination-independence-note">
            La coordination n'est jamais obligatoire pour louer de la décoration ou du matériel — et inversement.
          </p>
        </div>
      </section>

      {/* AVIS CLIENTS (CMS Avis — placement « coordination ») */}
      <ReviewsSection placement="coordination" title="Ils nous ont confié leur jour J" />

      <section className="bg-sage-light py-20 md:py-24">
        <div className="container-ae grid items-center gap-10 lg:grid-cols-2">
          <div>
            <HeartHandshake className="h-10 w-10 text-deepgreen" aria-hidden />
            <h2 className="mt-4 font-heading text-3xl text-deepgreen">Comment ça se passe ?</h2>
            <ol className="mt-6 space-y-4 text-sm text-foreground/80">
              {[
                "Vous prenez rendez-vous pour échanger sur votre mariage.",
                "Art Event prépare un devis personnalisé.",
                "Vous acceptez le devis, puis les CGV, et réglez l'acompte de 30 %.",
                "Votre réservation de coordination est confirmée — le suivi commence.",
                "Le solde est réglé au plus tard 7 jours avant l'événement.",
              ].map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-deepgreen text-xs font-bold text-cream">{i + 1}</span>
                  <span className="pt-1">{s}</span>
                </li>
              ))}
            </ol>
          </div>
          <img src={pageMedia["coordination:processus"]?.url || IMAGES.couple} alt={pageMedia["coordination:processus"]?.alt || "Coordination de mariage Art Event"} loading="lazy" className="rounded-xl object-cover shadow-lg" data-testid="coordination-process-image" />
        </div>
      </section>
    </div>
  );
}
